// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';

// Components
import EmptyTable from '../../../common/EmptyTable';
import RegattaCheckFields from '../../../fields/RegattaCheckFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  data: Array<$Model<'RegattaCheck'>>;
  loading: boolean;
};

class RegattaChecksSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaCheck') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaCheck({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const RegattaId = item.id;

    const newItem: $ModelUpsertParams<'RegattaCheck'> = {
      Name: '',
      RegattaId,
    };

    return (
      <SectionHeader
        bottomBorder
        description={t('Various checks that has to be passed by reagtta entries before the race')}
        iconClass={'icon-check'}
        rightButtons={(
          <FormDialog
            fields={(
              <RegattaCheckFields item={newItem} />
            )}
            id={'create-regatta-check-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaCheck'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertRegattaCheck(params);
              },
            })}
            subTitle={t('New')}
            title={t('Regatta check')}
          >
            <Button
              color={'info'}
              id={'create-regatta-check-button'}
              size={'sm'}
              title={t('Add regatta entry check')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        )}
        title={t('Regatta entry checks')}
      />
    );
  }

  renderButtons(dataItem: $Model<'RegattaCheck'>) {
    const {
      t,
    } = this.props;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegattaCheckFields item={dataItem} />
          )}
          id={`update-regatta-check-dialog-${dataItem.id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaCheck'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertRegattaCheck(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta entry check')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-check-button-${dataItem.id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-check-dialog-${dataItem.id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaCheck(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta entry check')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-check-button-${dataItem.id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>
              {t('Name')}
            </th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaCheck'>) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Name}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaChecksSectionView;
